import clsx from "clsx";
import type { FieldDefinition } from "../types";

/**
 * The field chrome is the design's: a small uppercase label, an underlined
 * control on no background, and a red rule with a red message under anything
 * that failed. The wrapper is the field's group: data-error on it turns its
 * control's rule and its message red.
 */

const labelType =
  "m-0 block p-0 text-[11px] leading-[1.4] font-medium tracking-[0.14em] text-brand-blue/53 uppercase";

/** The small uppercase label above a control. */
export const labelClass = `${labelType} mb-2`;

/**
 * The same label as a radio group's question. It is an h3 there, and on staging
 * the theme's h3[class] reset beat the label's margin, so it sits flush.
 */
export const groupLabelClass = labelType;

/**
 * The underlined control: input, textarea and select alike. The label already
 * says what the field is, so the placeholder repeating it would be noise; it
 * stays in the markup for anything reading the field out, and out of sight.
 *
 * A failed field keeps its red rule while focused, as the theme's did, which
 * is what it looks like when the form moves focus onto the error itself. The
 * shadow is only cleared off autofill, where the base styles paint one.
 */
export const controlClass = clsx(
  "block w-full rounded-none border-0 border-b-[1.5px] border-b-brand-blue/14 bg-transparent px-0 pt-[9px] pb-[11px]",
  "text-[15px] leading-[1.7] font-normal text-brand-blue not-autofill:shadow-none",
  "transition-[border-color] duration-180 ease-[ease] placeholder:text-transparent",
  "focus:border-b-brand-blue focus:outline-none",
  "group-data-[error=true]/field:border-b-[#b91c1c] group-data-[error=true]/field:focus:border-b-[#b91c1c]",
);

/** The radio and checkbox controls themselves, which are real ones, tinted. */
export const choiceInputClass =
  "m-0 size-4.5 shrink-0 cursor-pointer accent-brand-blue";

/** The statement beside a radio or a checkbox. */
export const choiceLabelClass =
  "m-0 cursor-pointer text-[15px] leading-[1.5] font-normal tracking-normal text-brand-blue normal-case";

/**
 * What every field partial takes: its ids and definition, and its live state
 * from the form that owns it.
 */
export type FieldProps = {
  /** Input id, unique on the page. Also the stem of the help and error ids. */
  id: string;
  /** Field name, as keyed in the field definitions. */
  name: string;
  field: FieldDefinition;
  /** "half" to sit in one column of a two-column row. */
  width?: string;
  /** Autofill token, e.g. "given-name". */
  autocomplete?: string;
  value: string;
  error?: string;
  /**
   * Whether a conditional field is up. Fields without a "required_if" rule
   * are always shown.
   */
  shown?: boolean;
  onChange: (name: string, value: string) => void;
  /** Focusing a field clears the error on it. */
  onFocus: (name: string) => void;
};

/** The field's own words for going unanswered, so the browser says what the server would. */
export function requiredMessage(field: FieldDefinition, conditional: boolean) {
  const messages = field.validation?.messages ?? {};

  // A conditional field is asked again in its required_if words, which is what the server sends.
  return (
    (conditional && messages.required_if) ||
    messages.required ||
    "This field is required."
  );
}

/**
 * The wrapper attributes every partial shares: its classes, its error state
 * (data-error, which the form also looks for to put focus on the first
 * failure), and the field's rules republished as data attributes, as the PHP
 * did.
 *
 * A field fills its row, or one of two columns of it when it is "half" width,
 * and fills its row again on a phone. The hidden attribute a conditional field
 * goes out with is honoured by preflight's [hidden] rule.
 */
export function wrapperProps({
  name,
  field,
  width,
  error,
  shown = true,
}: FieldProps) {
  const requiredIf = field.validation?.required_if;
  const value = requiredIf?.value;

  return {
    className: clsx(
      "group/field m-0 min-w-0 shrink-0 grow-0 border-0 p-0",
      width === "half"
        ? "basis-[calc(50%-16px)] max-w-[calc(50%-16px)] mobile:basis-full mobile:max-w-full"
        : "basis-full max-w-full",
      "js-field",
      requiredIf && "js-field-conditional",
    ),
    "data-error": !!error,
    "data-field": name,
    "data-required-message": requiredMessage(field, !!requiredIf),
    ...(requiredIf
      ? {
          "data-required-if-field": requiredIf.field,
          "data-required-if-value": Array.isArray(value)
            ? value.join(",")
            : (value ?? ""),
          "data-required-if-compare": requiredIf.compare ?? "==",
        }
      : {}),
    hidden: requiredIf ? !shown : undefined,
  };
}

/**
 * Whether the control is required right now: always for a required field, and
 * for a conditional one only while it is up, because the step validator would
 * otherwise stop the form on a field nobody can see.
 */
export function isRequiredNow(field: FieldDefinition, shown = true) {
  if (field.validation?.required) {
    return true;
  }

  return !!field.validation?.required_if && shown;
}

/** Both the hint and the error are announced with the input, so both are named. */
export function describedBy(id: string, field: FieldDefinition) {
  return [field.help_text ? `${id}-help` : "", `${id}-error`]
    .filter(Boolean)
    .join(" ");
}

/** The required asterisk, shown only for fields that are always required. */
export function RequiredMark({ field }: { field: FieldDefinition }) {
  if (!field.validation?.required) {
    return null;
  }

  return (
    <span className="ml-0.5 text-das-green" aria-hidden="true">
      *
    </span>
  );
}

export function HelpText({
  id,
  field,
}: {
  id: string;
  field: FieldDefinition;
}) {
  if (!field.help_text) {
    return null;
  }

  return (
    <p
      className="mx-0 -mt-1 mb-2 text-[12.5px] leading-[1.55] text-[#7a8a9a]"
      id={`${id}-help`}
    >
      {field.help_text}
    </p>
  );
}

/**
 * The error under a field, out of the layout until its field has failed. The
 * message goes into the text span rather than the error element itself, which
 * also holds the warning icon.
 *
 * The theme drew the icon with the Material Icons "error" ligature, but that
 * font is not loaded on this site, so it is the same glyph as an inline SVG.
 */
export function FieldError({ id, error }: { id: string; error?: string }) {
  return (
    <span
      className="mt-[5px] hidden items-center gap-[5px] text-[12px] leading-[1.5] text-[#b91c1c] group-data-[error=true]/field:flex js-field-error"
      id={`${id}-error`}
      role="alert"
    >
      <svg
        className="shrink-0 text-[14px] leading-none"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
      </svg>

      <span className="js-field-error-text">{error || ""}</span>
    </span>
  );
}
